// Team Context - Manages team state and provides team-related functionality

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { 
  Team, 
  TeamContextType, 
  CreateTeamRequest, 
  InviteToTeamRequest,
  TeamPermission,
  TeamRole,
  hasTeamPermission,
  TeamsListResponse,
  TeamResponse,
  TeamInvitationResponse
} from '../types/teams';

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeam = (): TeamContextType => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

interface TeamProviderProps {
  children: React.ReactNode;
}

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Computed properties
  const isPersonalMode = currentTeam === null;

  // Load user's teams
  const loadTeams = useCallback(async () => {
    // Only load teams if user is authenticated
    if (!isAuthenticated || !user) {
      console.log('TeamContext: User not authenticated, skipping team load');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/teams/list', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load teams');
      }

      const data: TeamsListResponse = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load teams');
      }

      setUserTeams(data.teams);

      // If current team is no longer available, switch to personal mode
      if (currentTeam && !data.teams.find(t => t.id === currentTeam.id)) {
        setCurrentTeam(null);
        localStorage.removeItem('selectedTeamId');
      }

    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Switch to a specific team
  const switchToTeam = useCallback((teamId: string) => {
    const team = userTeams.find(t => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
      localStorage.setItem('selectedTeamId', teamId);
      setError(null);
    } else {
      setError('Team not found');
    }
  }, [userTeams]);

  // Switch to personal mode
  const switchToPersonal = useCallback(() => {
    setCurrentTeam(null);
    localStorage.removeItem('selectedTeamId');
    setError(null);
  }, []);

  // Create a new team
  const createTeam = useCallback(async (data: CreateTeamRequest): Promise<Team | null> => {
    if (!isAuthenticated || !user) {
      setError('Authentication required');
      return null;
    }

    try {
      setError(null);

      const response = await fetch('/api/teams/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to create team');
      }

      const result: TeamResponse = await response.json();
      
      if (!result.success || !result.team) {
        throw new Error(result.error || 'Failed to create team');
      }

      // Add new team to the list
      setUserTeams(prev => [result.team!, ...prev]);
      
      // Switch to the new team
      setCurrentTeam(result.team!);
      localStorage.setItem('selectedTeamId', result.team!.id);

      return result.team!;

    } catch (err) {
      console.error('Error creating team:', err);
      setError(err instanceof Error ? err.message : 'Failed to create team');
      return null;
    }
  }, [isAuthenticated, user]);

  // Invite user to team
  const inviteToTeam = useCallback(async (teamId: string, data: InviteToTeamRequest): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);

      const response = await fetch(`/api/teams/${teamId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to send invitation');
      }

      const result: TeamInvitationResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send invitation');
      }

      // Reload teams to get updated member count
      await loadTeams();

      return true;

    } catch (err) {
      console.error('Error inviting to team:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
      return false;
    }
  }, [loadTeams, isAuthenticated, user]);

  // Delete team
  const deleteTeam = useCallback(async (teamId: string): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);

      const response = await fetch(`/api/teams/${teamId}/delete`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete team');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete team');
      }

      // Remove team from local state
      setUserTeams(prev => prev.filter(t => t.id !== teamId));
      
      // If deleted team was current team, switch to personal mode
      if (currentTeam?.id === teamId) {
        setCurrentTeam(null);
        localStorage.removeItem('selectedTeamId');
      }

      return true;

    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete team');
      return false;
    }
  }, [isAuthenticated, user, currentTeam]);

  // Remove team member
  const removeMember = useCallback(async (teamId: string, memberId: string): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);

      const response = await fetch(`/api/teams/${teamId}/members/${memberId}/remove`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to remove team member');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove team member');
      }

      // Reload teams to get updated member count
      await loadTeams();

      return true;

    } catch (err) {
      console.error('Error removing team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove team member');
      return false;
    }
  }, [loadTeams, isAuthenticated, user]);

  // Check if current user has specific permission in current team
  const hasPermission = useCallback((permission: TeamPermission): boolean => {
    if (isPersonalMode) {
      // In personal mode, user has all permissions for their own content
      return true;
    }

    return hasTeamPermission(currentTeam?.user_role || null, permission);
  }, [currentTeam, isPersonalMode]);

  // Get current user's role in current team
  const getUserRole = useCallback((): TeamRole | null => {
    return currentTeam?.user_role || null;
  }, [currentTeam]);

  // Check if current user is team owner
  const isTeamOwner = useCallback((): boolean => {
    return currentTeam?.user_role === 'owner';
  }, [currentTeam]);

  // Check if current user is team admin (owner or admin)
  const isTeamAdmin = useCallback((): boolean => {
    const role = currentTeam?.user_role;
    return role === 'owner' || role === 'admin';
  }, [currentTeam]);

  // Load teams when user becomes authenticated (only once)
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('TeamContext: User authenticated, loading teams');
      loadTeams();
    } else {
      // Clear teams when user is not authenticated
      setUserTeams([]);
      setCurrentTeam(null);
      setError(null);
      localStorage.removeItem('selectedTeamId');
    }
  }, [isAuthenticated, user?.id]);

  // Restore selected team from localStorage when teams are loaded
  useEffect(() => {
    if (userTeams.length > 0) {
      const savedTeamId = localStorage.getItem('selectedTeamId');
      if (savedTeamId) {
        const savedTeam = userTeams.find(t => t.id === savedTeamId);
        if (savedTeam) {
          setCurrentTeam(savedTeam);
        } else {
          localStorage.removeItem('selectedTeamId');
        }
      }
    }
  }, [userTeams]);

  // Update current team when userTeams changes (to get updated member counts, etc.)
  useEffect(() => {
    if (currentTeam) {
      const updatedTeam = userTeams.find(t => t.id === currentTeam.id);
      if (updatedTeam) {
        setCurrentTeam(updatedTeam);
      }
    }
  }, [userTeams, currentTeam?.id]);

  const contextValue: TeamContextType = {
    // State
    currentTeam,
    userTeams,
    isPersonalMode,
    loading,
    error,

    // Actions
    loadTeams,
    switchToTeam,
    switchToPersonal,
    createTeam,
    inviteToTeam,
    deleteTeam,
    removeMember,

    // Utilities
    hasPermission,
    getUserRole,
    isTeamOwner,
    isTeamAdmin
  };

  return (
    <TeamContext.Provider value={contextValue}>
      {children}
    </TeamContext.Provider>
  );
}; 